import type { OnboardingStepProps } from "./Onboarding";

import React from "react";

import Input from "@common/designSystem/Input";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import twMerge from "@replo/design-system/utils/twMerge";
import { z } from "zod";

import OnboardingFooter from "./OnboardingFooter";

const OnboardingUserInfo: React.FC<OnboardingStepProps> = ({
  currentStep,
  submitStep,
  isLoading,
  totalSteps,
}) => {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const [firstName, setFirstName] = useLocalStorageState(
    "onboarding-firstName",
    "",
    { schema: z.string() },
  );
  const [lastName, setLastName] = useLocalStorageState(
    "onboarding-lastName",
    "",
    { schema: z.string() },
  );
  const [teamDescription, setTeamDescription] = useLocalStorageState(
    "onboarding-teamDescription",
    null,
    { schema: z.enum(["business", "agency", "freelancer"]).nullable() },
  );

  const handleSubmit = () => {
    logEvent("onboarding.flow.begin", {});
    if (user) {
      logEvent("onboarding.flow.step.completed", {
        userId: user.email,
        stepId: "user-profile",
        stepType: "user-profile",
        stepData: {
          firstName,
          lastName,
          teamDescription,
        },
      });
    }
    submitStep();
  };

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="flex-1 overflow-y-auto flex flex-col items-center md:py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="pt-14 md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-12 md:gap-3">
          <h1 className="header-h1 text-3xl md:text-4xl">
            Let's Get To Know You
          </h1>
          <p className="text-muted header-h1 text-lg font-normal">
            We'll use this info to create your user profile.
          </p>
        </div>
        <div className="md:w-[640px] w-full flex flex-col gap-10 pb-25 md:pb-40 px-5 md:px-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <label className="text-default text-sm md:text-base font-medium">
                First Name{" "}
                {firstName === "" && <span className="text-danger">*</span>}
              </label>
              <Input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                size="base"
                placeholder="Enter your first name"
              />
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-default text-sm md:text-base font-medium">
                Last Name{" "}
                {lastName === "" && <span className="text-danger">*</span>}
              </label>
              <Input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                size="base"
                placeholder="Enter your last name"
              />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <label className="text-default text-sm md:text-base font-medium">
              What best describes you and your team?{" "}
              {teamDescription === null && (
                <span className="text-danger">*</span>
              )}
            </label>
            <div className="flex flex-col gap-4 h-96">
              <div
                className="px-6 py-4 rounded-xl border-0.5 border-border-component bg-sheet flex-1 flex flex-col justify-center gap-2 relative cursor-pointer"
                onClick={() => setTeamDescription("business")}
              >
                <img
                  src="/images/onboarding/onboarding-background.png"
                  alt=""
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                    teamDescription === "business"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="pr-35">
                  <h3
                    className={twMerge(
                      "text-base font-semibold relative z-10 transition-colors duration-300",
                      teamDescription !== "business" && "text-muted",
                    )}
                    style={
                      teamDescription === "business" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    Business
                  </h3>
                  <p
                    className={twMerge(
                      "text-sm relative z-10 transition-colors duration-300",
                      teamDescription !== "business" && "text-muted",
                    )}
                    style={
                      teamDescription === "business" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    I sell directly to consumers online.
                  </p>
                </div>
                <img
                  src="/images/onboarding/business-grey.png"
                  alt="Business"
                  className="h-35 w-35 object-contain absolute bottom-[-17px] right-10 z-10"
                />
                <img
                  src="/images/onboarding/business-color.png"
                  alt="Business"
                  className={twMerge(
                    "h-28 w-35 object-contain absolute bottom-[-4px] right-10 z-10 transition-opacity duration-300",
                    teamDescription === "business"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
              </div>
              <div
                className="px-6 py-4 rounded-xl border-0.5 border-border-component bg-sheet flex-1 flex flex-col justify-center gap-2 relative cursor-pointer"
                onClick={() => setTeamDescription("agency")}
              >
                <img
                  src="/images/onboarding/onboarding-background.png"
                  alt=""
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                    teamDescription === "agency" ? "opacity-100" : "opacity-0",
                  )}
                />
                <div className="pr-35">
                  <h3
                    className={twMerge(
                      "text-base font-semibold relative z-10 transition-colors duration-300",
                      teamDescription !== "agency" && "text-muted",
                    )}
                    style={
                      teamDescription === "agency" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    Agency
                  </h3>
                  <p
                    className={twMerge(
                      "text-sm relative z-10 transition-colors duration-300",
                      teamDescription !== "agency" && "text-muted",
                    )}
                    style={
                      teamDescription === "agency" ? { color: "#F5FAD9" } : {}
                    }
                  >
                    I'm part of an agency that helps clients achieve their
                    commerce goals.
                  </p>
                </div>
                <img
                  src="/images/onboarding/agency-grey.png"
                  alt="Agency"
                  className="h-30 w-30 object-contain absolute bottom-[-12px] right-12 z-10"
                />
                <img
                  src="/images/onboarding/agency-color.png"
                  alt="Agency"
                  className={twMerge(
                    "h-30 w-30 object-contain absolute bottom-[-20px] right-12 z-10 transition-opacity duration-300",
                    teamDescription === "agency" ? "opacity-100" : "opacity-0",
                  )}
                />
              </div>
              <div
                className="px-6 py-4 rounded-xl border-0.5 border-border-component bg-sheet flex-1 flex flex-col justify-center gap-2 relative cursor-pointer"
                onClick={() => setTeamDescription("freelancer")}
              >
                <img
                  src="/images/onboarding/onboarding-background.png"
                  alt=""
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-xl transition-opacity duration-300",
                    teamDescription === "freelancer"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="pr-35">
                  <h3
                    className={twMerge(
                      "text-base font-semibold relative z-10 transition-colors duration-300",
                      teamDescription !== "freelancer" && "text-muted",
                    )}
                    style={
                      teamDescription === "freelancer"
                        ? { color: "#F5FAD9" }
                        : {}
                    }
                  >
                    Freelancer
                  </h3>
                  <p
                    className={twMerge(
                      "text-sm relative z-10 transition-colors duration-300",
                      teamDescription !== "freelancer" && "text-muted",
                    )}
                    style={
                      teamDescription === "freelancer"
                        ? { color: "#F5FAD9" }
                        : {}
                    }
                  >
                    I work independently to help clients achieve their commerce
                    goals.
                  </p>
                </div>
                <img
                  src="/images/onboarding/freelancer-grey.png"
                  alt="Freelancer"
                  className="h-30 w-30 object-contain absolute bottom-[-10px] right-8 z-10"
                />
                <img
                  src="/images/onboarding/freelancer-color.png"
                  alt="Freelancer"
                  className={twMerge(
                    "h-30 w-30 object-contain absolute bottom-[-10px] right-8 z-10 transition-opacity duration-300",
                    teamDescription === "freelancer"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute right-0 bottom-0 left-0 z-20">
        <OnboardingFooter
          currentStep={currentStep}
          onNext={handleSubmit}
          isNextDisabled={
            isLoading ||
            firstName === "" ||
            lastName === "" ||
            teamDescription === null
          }
          className="w-full md:max-w-[640px] px-5 md:px-0"
          isNextLoading={isLoading}
          totalSteps={totalSteps}
        />
      </div>
    </div>
  );
};

export default OnboardingUserInfo;
